import { useState } from "react";
import { Button } from "../../ui";
import ExcelJS from "exceljs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

interface MotorData {
  deviceName: string;
  timestamp: string;
  data: {
    engineStatus?: any;
    checkEngineLightStatus?: any;
    odometerECU?: any;
    fuelLevel?: any;
    speed?: any;
    rpm?: any;
    engineLoad?: any;
    intakeAirPressure?: any;
    intakeAirTemperature?: any;
    barometricPressure?: any;
    batteryVoltage?: any;
    fuelPressure?: any;
    coolantTemperature?: any;
    powerTakeoff?: any;
  };
}

interface MotorInfoBlockProps {
  motorData?: MotorData;
}

export default function MotorInfoBlock({ motorData }: MotorInfoBlockProps) {
  const [isDownloading, setIsDownloading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const exportToPdf = async () => {
    if (!motorData) return;

    try {
      setIsDownloading(true);
      const doc = new jsPDF({ orientation: "portrait" });
      const pageWidth = doc.internal.pageSize.getWidth();

      // Header
      doc.setFillColor(59, 130, 246);
      doc.rect(0, 0, pageWidth, 22, "F");

      doc.setFontSize(28);
      doc.setTextColor(255, 255, 255);
      doc.setFont(undefined, "bold");
      doc.text("WAYAK", pageWidth / 2, 15, { align: "center" });

      // Title
      doc.setFontSize(16);
      doc.setTextColor(59, 130, 246);
      doc.setFont(undefined, "normal");
      doc.text("Información del Motor", pageWidth / 2, 32, { align: "center" });

      // Device info
      doc.setFontSize(10);
      doc.setTextColor(100, 100, 100);
      doc.text(`Dispositivo: ${motorData.deviceName}`, 14, 40);
      doc.text(`Fecha: ${motorData.timestamp}`, 14, 47);

      // Table data
      const motorRows = [
        { label: "Estado del Motor", value: motorData.data.engineStatus || "No disponible" },
        { label: "Check Engine Light", value: motorData.data.checkEngineLightStatus || "No disponible" },
        { label: "Odómetro (ECU)", value: `${motorData.data.odometerECU || "N/A"} km` },
        { label: "Nivel de Combustible", value: `${motorData.data.fuelLevel || "N/A"}%` },
        { label: "Velocidad", value: `${motorData.data.speed || "N/A"} km/h` },
        { label: "RPM", value: `${motorData.data.rpm || "N/A"}` },
        { label: "Carga del Motor", value: `${motorData.data.engineLoad || "N/A"}%` },
        { label: "Presión Aire Entrada", value: `${motorData.data.intakeAirPressure || "N/A"} bar` },
        { label: "Temperatura Aire Entrada", value: `${motorData.data.intakeAirTemperature || "N/A"}°C` },
        { label: "Presión Barométrica", value: `${motorData.data.barometricPressure || "N/A"} mbar` },
        { label: "Voltaje Batería", value: `${motorData.data.batteryVoltage || "N/A"} V` },
        { label: "Presión Combustible", value: `${motorData.data.fuelPressure || "N/A"} bar` },
        { label: "Temperatura Refrigerante", value: `${motorData.data.coolantTemperature || "N/A"}°C` },
        { label: "Power Takeoff", value: motorData.data.powerTakeoff || "No disponible" },
      ];

      autoTable(doc, {
        startY: 54,
        head: [["Parámetro", "Valor"]],
        body: motorRows.map(row => [row.label, String(row.value)]),
        styles: { fontSize: 9, cellPadding: 3 },
        headStyles: { fillColor: [59, 130, 246], textColor: [255, 255, 255], fontStyle: "bold" },
        alternateRowStyles: { fillColor: [245, 245, 245] },
        columnStyles: { 0: { cellWidth: 90 }, 1: { cellWidth: 80 } },
      });

      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      const sanitizedName = motorData.deviceName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      doc.save(`motor-info_${sanitizedName}_${dateStr}.pdf`);
    } catch (error) {
      console.error("[MotorInfoBlock] PDF export error:", error);
      setError("Error al generar el PDF");
    } finally {
      setIsDownloading(false);
    }
  };

  const exportToExcel = async () => {
    if (!motorData) return;

    try {
      setIsDownloading(true);
      const workbook = new ExcelJS.Workbook();
      const worksheet = workbook.addWorksheet("Motor Info");

      const headerStyle = {
        fill: { type: "pattern", pattern: "solid", fgColor: { argb: "FF3B82F6" } },
        font: { bold: true, color: { argb: "FFFFFFFF" } },
        alignment: { horizontal: "center", vertical: "center" },
        border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } },
      };

      const dataStyle = {
        border: { top: { style: "thin" }, left: { style: "thin" }, bottom: { style: "thin" }, right: { style: "thin" } },
      };

      worksheet.addRow([]).height = 5;
      const titleRow = worksheet.addRow(["Información del Motor"]);
      titleRow.font = { bold: true, size: 14 };
      worksheet.mergeCells("A2:B2");

      worksheet.addRow([]).height = 5;
      worksheet.addRow([`Dispositivo: ${motorData.deviceName}`]);
      worksheet.addRow([`Fecha: ${motorData.timestamp}`]);
      worksheet.addRow([]).height = 5;

      const headerRow = worksheet.addRow(["Parámetro", "Valor"]);
      headerRow.eachCell((cell: any) => {
        cell.style = headerStyle;
      });

      const motorData_arr = [
        ["Estado del Motor", motorData.data.engineStatus || "No disponible"],
        ["Check Engine Light", motorData.data.checkEngineLightStatus || "No disponible"],
        ["Odómetro (ECU)", `${motorData.data.odometerECU || "N/A"} km`],
        ["Nivel de Combustible", `${motorData.data.fuelLevel || "N/A"}%`],
        ["Velocidad", `${motorData.data.speed || "N/A"} km/h`],
        ["RPM", `${motorData.data.rpm || "N/A"}`],
        ["Carga del Motor", `${motorData.data.engineLoad || "N/A"}%`],
        ["Presión Aire Entrada", `${motorData.data.intakeAirPressure || "N/A"} bar`],
        ["Temperatura Aire Entrada", `${motorData.data.intakeAirTemperature || "N/A"}°C`],
        ["Presión Barométrica", `${motorData.data.barometricPressure || "N/A"} mbar`],
        ["Voltaje Batería", `${motorData.data.batteryVoltage || "N/A"} V`],
        ["Presión Combustible", `${motorData.data.fuelPressure || "N/A"} bar`],
        ["Temperatura Refrigerante", `${motorData.data.coolantTemperature || "N/A"}°C`],
        ["Power Takeoff", motorData.data.powerTakeoff || "No disponible"],
      ];

      motorData_arr.forEach(row => {
        const newRow = worksheet.addRow(row);
        newRow.eachCell((cell: any) => {
          cell.style = dataStyle;
        });
      });

      worksheet.columns[0].width = 30;
      worksheet.columns[1].width = 25;

      const buffer = await workbook.xlsx.writeBuffer();
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;

      const today = new Date();
      const dateStr = today.toISOString().split("T")[0];
      const sanitizedName = motorData.deviceName.replace(/[^a-z0-9]/gi, "_").toLowerCase();
      link.download = `motor-info_${sanitizedName}_${dateStr}.xlsx`;
      link.rel = "noopener noreferrer";
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error("[MotorInfoBlock] Excel export error:", error);
      setError("Error al generar el Excel");
    } finally {
      setIsDownloading(false);
    }
  };

  if (!motorData) {
    return (
      <div className="mt-3 rounded-lg bg-yellow-50 p-3 text-sm text-yellow-700">
        ⚠️ No hay datos de motor disponibles
      </div>
    );
  }

  if (error) {
    return (
      <div className="mt-3 rounded-lg bg-red-50 p-3 text-sm text-red-700">
        ❌ {error}
      </div>
    );
  }

  return (
    <div className="mt-4 rounded-lg border border-line bg-app/50 p-4 space-y-3">
      <div className="text-sm text-content">
        <p className="font-medium mb-2">📋 Información del Motor - {motorData.deviceName}</p>
        <p className="text-xs text-content-secondary mb-3">Timestamp: {motorData.timestamp}</p>
      </div>

      <div className="flex gap-2">
        <Button
          onClick={() => exportToPdf()}
          disabled={isDownloading}
          className="flex-1 flex items-center justify-center gap-2 bg-red-600 hover:bg-red-700 disabled:bg-gray-400 text-white text-sm"
          icon={<i className={isDownloading ? "ri-loader-4-line animate-spin" : "ri-file-pdf-2-line"} />}
        >
          {isDownloading ? "Descargando..." : "PDF"}
        </Button>
        <Button
          onClick={() => exportToExcel()}
          disabled={isDownloading}
          className="flex-1 flex items-center justify-center gap-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-400 text-white text-sm"
          icon={<i className={isDownloading ? "ri-loader-4-line animate-spin" : "ri-file-excel-2-line"} />}
        >
          {isDownloading ? "Descargando..." : "Excel"}
        </Button>
      </div>
    </div>
  );
}
